/**
 * Haptic Feedback Utility
 * 
 * Cross-platform haptic feedback API wrapper for Web, iOS, and Android.
 * Provides tactile feedback for user interactions to enhance UX.
 * 
 * Supported Platforms:
 * - iOS: Haptic Feedback API (Safari 13+)
 * - Android: Vibration API
 * - Web: Fallback to Vibration API
 * - Desktop: Silent fallback (no-op)
 * 
 * @version 1.0.0
 */

/**
 * Haptic intensity patterns
 */
export type HapticIntensity = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

/**
 * Vibration patterns (in milliseconds)
 */
const VIBRATION_PATTERNS: Record<HapticIntensity, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
  success: [10, 50, 10], // Double tap pattern
  warning: [20, 100, 20], // Double tap with pause
  error: [50, 100, 50, 100, 50], // Triple tap pattern
};

/**
 * Check if haptic feedback is supported
 */
export function isHapticSupported(): boolean {
  return (
    'vibrate' in navigator ||
    'hapticEngine' in window ||
    typeof (window as any).TapticEngine !== 'undefined'
  );
}

/**
 * Check if on iOS device
 */
function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Trigger haptic feedback
 * 
 * @param intensity - Feedback intensity pattern
 * @param force - Force trigger even if user disabled haptics (use sparingly)
 * 
 * @example
 * // Light feedback for button taps
 * triggerHaptic('light');
 * 
 * // Success pattern for completed actions
 * triggerHaptic('success');
 * 
 * // Error pattern for failures
 * triggerHaptic('error');
 */
export function triggerHaptic(intensity: HapticIntensity = 'light', force = false): void {
  // Check if haptics are disabled by user (stored in localStorage)
  if (!force && localStorage.getItem('haptics-disabled') === 'true') {
    return;
  }

  // Try iOS Haptic Feedback API first (best experience)
  if (isIOS() && 'hapticEngine' in window) {
    try {
      const hapticEngine = (window as any).hapticEngine;
      
      // Map intensity to iOS haptic type
      const hapticTypes: Record<HapticIntensity, string> = {
        light: 'impactLight',
        medium: 'impactMedium',
        heavy: 'impactHeavy',
        success: 'notificationSuccess',
        warning: 'notificationWarning',
        error: 'notificationError',
      };

      const hapticType = hapticTypes[intensity];
      if (hapticType && typeof hapticEngine[hapticType] === 'function') {
        hapticEngine[hapticType]();
        return;
      }
    } catch (err) {
      console.warn('iOS Haptic Feedback failed:', err);
      // Fall through to Vibration API
    }
  }

  // Fallback to Vibration API (Android, modern browsers)
  if ('vibrate' in navigator) {
    try {
      const pattern = VIBRATION_PATTERNS[intensity];
      navigator.vibrate(pattern);
    } catch (err) {
      console.warn('Vibration API failed:', err);
    }
  }
}

/**
 * Cancel any ongoing haptic feedback
 */
export function cancelHaptic(): void {
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(0);
    } catch (err) {
      console.warn('Failed to cancel haptic:', err);
    }
  }
}

/**
 * Enable haptic feedback (removes localStorage flag)
 */
export function enableHaptics(): void {
  localStorage.removeItem('haptics-disabled');
}

/**
 * Disable haptic feedback (sets localStorage flag)
 */
export function disableHaptics(): void {
  localStorage.setItem('haptics-disabled', 'true');
}

/**
 * Check if haptics are currently enabled
 */
export function areHapticsEnabled(): boolean {
  return localStorage.getItem('haptics-disabled') !== 'true';
}

/**
 * React Hook for haptic feedback
 * 
 * @example
 * const haptic = useHaptic();
 * 
 * <button onClick={() => haptic('light')}>
 *   Tap me
 * </button>
 */
export function useHaptic() {
  return triggerHaptic;
}

/**
 * Higher-Order Component to add haptic feedback to clicks
 * 
 * @example
 * const HapticButton = withHaptic('button', 'light');
 * <HapticButton onClick={handleClick}>Click me</HapticButton>
 */
export function withHaptic<P extends { onClick?: () => void }>(
  Component: React.ComponentType<P>,
  intensity: HapticIntensity = 'light'
) {
  return (props: P) => {
    const handleClick = () => {
      triggerHaptic(intensity);
      if (props.onClick) {
        props.onClick();
      }
    };

    return <Component {...props} onClick={handleClick} />;
  };
}

/**
 * Haptic feedback for common UI patterns
 */
export const hapticPresets = {
  /** Button tap */
  buttonTap: () => triggerHaptic('light'),
  
  /** Toggle switch */
  toggle: () => triggerHaptic('medium'),
  
  /** Delete action */
  delete: () => triggerHaptic('heavy'),
  
  /** Success confirmation */
  success: () => triggerHaptic('success'),
  
  /** Warning alert */
  warning: () => triggerHaptic('warning'),
  
  /** Error alert */
  error: () => triggerHaptic('error'),
  
  /** Swipe gesture start */
  swipeStart: () => triggerHaptic('light'),
  
  /** Swipe gesture end */
  swipeEnd: () => triggerHaptic('medium'),
  
  /** Long press */
  longPress: () => triggerHaptic('heavy'),
  
  /** Modal open */
  modalOpen: () => triggerHaptic('light'),
  
  /** Modal close */
  modalClose: () => triggerHaptic('light'),
  
  /** Notification received */
  notification: () => triggerHaptic('medium'),
};

export default {
  trigger: triggerHaptic,
  cancel: cancelHaptic,
  enable: enableHaptics,
  disable: disableHaptics,
  isEnabled: areHapticsEnabled,
  isSupported: isHapticSupported,
  presets: hapticPresets,
};